import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FeedbackIcon from "@material-ui/icons/Feedback";
import utils from "../../universal/utils";
import adminFetch from "../../universal/adminFetch";
import useIsMountedRef from "../../hooks/useIsMountedRef";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  commentCell: {
    whiteSpace: "pre-wrap",
    maxWidth: 420,
  },
}));

function formatBytes(size) {
  if (!size) return "";
  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export default function UiExperimentsFeedbackPanel({ userId, refreshToken = 0 }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [openingAttachmentId, setOpeningAttachmentId] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const response = await adminFetch(
        `${basePath}/api/v7/admin/ui-experiments/v2/feedback/adpass/${userId}?limit=100`
      );
      const data = await response.json();
      if (!data?.ok) {
        throw new Error(data?.error || "Failed to load feedback");
      }
      if (isMountedRef.current) {
        setItems(Array.isArray(data.items) ? data.items : []);
      }
    } catch (e) {
      if (isMountedRef.current) {
        setError(e?.message || String(e));
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [basePath, isMountedRef, userId]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  const openAttachment = useCallback(
    async (item) => {
      if (!userId || !item?.id) return;

      setOpeningAttachmentId(item.id);
      setError("");
      try {
        const response = await adminFetch(
          `${basePath}/api/v7/admin/ui-experiments/v2/feedback/${item.id}/media/adpass/${userId}`
        );
        if (!response.ok) {
          let message = "Failed to open attachment";
          try {
            const data = await response.json();
            message = data?.error || message;
          } catch (parseError) {
            // Ignore JSON parse errors for non-JSON error bodies.
          }
          throw new Error(message);
        }

        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        window.open(objectUrl, "_blank", "noopener,noreferrer");
        window.setTimeout(() => {
          window.URL.revokeObjectURL(objectUrl);
        }, 60_000);
      } catch (e) {
        if (isMountedRef.current) {
          setError(e?.message || String(e));
        }
      } finally {
        if (isMountedRef.current) {
          setOpeningAttachmentId(null);
        }
      }
    },
    [basePath, isMountedRef, userId]
  );

  return (
    <Paper className={classes.paper} elevation={1}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={16}>
        <Box display="flex" alignItems="center" gridGap={8}>
          <FeedbackIcon color="primary" />
          <Typography variant="h6">UI Feedback</Typography>
        </Box>
        <Button variant="outlined" color="primary" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" marginTop={3}>
          <CircularProgress />
        </Box>
      ) : null}

      {error ? (
        <Typography color="error" style={{ marginTop: 16 }}>
          {error}
        </Typography>
      ) : null}

      <TableContainer className={classes.tableContainer} component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Page</TableCell>
              <TableCell>Comment</TableCell>
              <TableCell>Attachment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  {item.createdAt ? new Date(item.createdAt).toLocaleString() : "—"}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={item.kind === "bug" ? "Bug" : "Feedback"}
                    color={item.kind === "bug" ? "secondary" : "default"}
                  />
                </TableCell>
                <TableCell>
                  {item.nickname || item.userId}
                  {item.designLane ? (
                    <Typography variant="caption" display="block" color="textSecondary">
                      {item.designLane} layout
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{item.pagePath || "—"}</TableCell>
                <TableCell className={classes.commentCell}>{item.comment}</TableCell>
                <TableCell>
                  {item.media?.hasMedia ? (
                    <Link
                      component="button"
                      type="button"
                      onClick={() => openAttachment(item)}
                      disabled={openingAttachmentId === item.id}
                      style={{
                        cursor: openingAttachmentId === item.id ? "wait" : "pointer",
                      }}
                    >
                      {openingAttachmentId === item.id ? "Opening…" : item.media.fileName}
                      {item.media.size ? ` (${formatBytes(item.media.size)})` : ""}
                    </Link>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!items.length && !loading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="textSecondary">
                    No feedback submitted yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

UiExperimentsFeedbackPanel.propTypes = {
  userId: PropTypes.string,
  refreshToken: PropTypes.number,
};
